import React, { memo, type ReactNode } from 'react';
import { type PreloadedQuery, graphql, useFragment } from 'react-relay';
import { SmartCardProvider } from '@atlaskit/link-provider';
import client from '@atlassian/jira-apollo-gira/src/index.tsx';
import { ApolloClientProvider } from '@atlassian/jira-apollo-multiple-clients/src/main.tsx';
import BusinessAppWrapper from '@atlassian/jira-business-app-wrapper/src/ui/app-wrapper/index.tsx';
import { ConfluenceModalProvider } from '@atlassian/jira-business-confluence-pages/src/ui/confluence-view-modal/index.tsx';
import { useTenantHasConfluence } from '@atlassian/jira-business-confluence-pages/src/utils/use-tenant-has-confluence/index.tsx';
import { useProject } from '@atlassian/jira-business-entity-project-hook/src/index.tsx';
import { IssueTypesAndFieldsContext } from '@atlassian/jira-business-entity-project/src/controllers/issue-types-and-fields-context/index.tsx';
import { RefetchFieldsController } from '@atlassian/jira-business-entity-project/src/controllers/refetch-fields/index.tsx';
import { ExperienceErrorBoundary } from '@atlassian/jira-business-experience-tracking/src/controllers/experience-tracker/index.tsx';
import { IssueCreateProvider } from '@atlassian/jira-business-issue-create/src/controllers/issue-create-context/IssueCreateProvider.tsx';
import { WorkflowsProvider } from '@atlassian/jira-business-workflows/src/controllers/workflows-context/index.tsx';
import LoginRedirect from '@atlassian/jira-common-navigation/src/login-redirect/index.tsx';
import { AuthenticationErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/authentication-error-boundary/AuthenticationErrorBoundary.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/JSErrorBoundary.tsx';
import { UNSAFE_noExposureExp, expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { STATUS_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	SCREEN,
} from '@atlassian/jira-product-analytics-bridge';
import type { formatRulesQuery as FormattingRulesQuery } from '@atlassian/jira-relay/src/__generated__/formatRulesQuery.graphql';
import type { src_businessList_ListViewRoot_queryFragment$key } from '@atlassian/jira-relay/src/__generated__/src_businessList_ListViewRoot_queryFragment.graphql';
import type { src_businessList_ListViewRootWithSmartCard_queryFragment$key } from '@atlassian/jira-relay/src/__generated__/src_businessList_ListViewRootWithSmartCard_queryFragment.graphql';
import type { src_businessList_ListViewWithWorkflow_queryFragment$key } from '@atlassian/jira-relay/src/__generated__/src_businessList_ListViewWithWorkflow_queryFragment.graphql';
import type { src_businessList_List_queryFragment$key } from '@atlassian/jira-relay/src/__generated__/src_businessList_List_queryFragment.graphql';
import { VIEW_EXPERIENCE } from './common/constants.tsx';
import { InvalidJQLErrorBoundary } from './common/ui/invalid-filter-error-boundary/index.tsx';
import { useJWMListFeatures } from './controllers/features-context/index.tsx';
import { FormsLabelFlag } from './controllers/forms-label-flag/index.tsx';
import { useGroupBy } from './controllers/group-by/index.tsx';
import { useCreateIssue } from './services/create-issue/index.tsx';
import { EmptyState } from './ui/list-view/empty-state/index.tsx';
import ListView from './ui/list-view/index.tsx';

const analyticsSourceName = 'listView';

const ListViewWithWorkflow = memo(
	({
		formattingRulesQuery,
		queryFragment,
	}: {
		queryFragment: src_businessList_ListViewWithWorkflow_queryFragment$key;
		formattingRulesQuery?: PreloadedQuery<FormattingRulesQuery> | null;
	}) => {
		const projectData = useProject();
		const projectId = String(projectData.id);
		const projectKey = projectData.key;
		const projectName = projectData.name;
		const isTeamManaged = projectData.isSimplified;
		const [groupByFieldId] = useGroupBy();
		const isGroupingByStatus = groupByFieldId === STATUS_TYPE;
		const services = useCreateIssue();

		const data = useFragment(
			graphql`
				fragment src_businessList_ListViewWithWorkflow_queryFragment on Query {
					...IssueCreateProvider
				}
			`,
			queryFragment,
		);

		return (
			<WorkflowsProvider enabled={isGroupingByStatus}>
				<IssueCreateProvider
					onCreateIssue={services.createIssue}
					onRefetchIssue={services.refetchIssue}
					queryFragment={data}
				>
					<ContextualAnalyticsData
						objectType="list"
						sourceType={SCREEN}
						sourceName={analyticsSourceName}
					>
						<ListView
							projectKey={projectKey}
							projectName={projectName}
							projectId={projectId}
							isTeamManaged={isTeamManaged}
							formattingRulesQuery={formattingRulesQuery}
						/>
						<FireScreenAnalytics attributes={{ projectKey }} />
					</ContextualAnalyticsData>
				</IssueCreateProvider>
			</WorkflowsProvider>
		);
	},
);
export const ListViewRoot = memo(
	({
		queryFragment,
		header,
		formattingRulesQuery,
	}: {
		queryFragment: src_businessList_ListViewRoot_queryFragment$key;
		header?: ReactNode;
		formattingRulesQuery?: PreloadedQuery<FormattingRulesQuery> | null;
	}) => {
		const { isSoftwareFieldsEnabled } = useJWMListFeatures();

		// NOTE: Don't want to fire exposures here - happens in form directory view
		const [formsIssueViewExpConfig] = UNSAFE_noExposureExp('jwm_business_forms_issue_view');

		const data = useFragment(
			graphql`
				fragment src_businessList_ListViewRoot_queryFragment on Query {
					...src_businessList_ListViewWithWorkflow_queryFragment
				}
			`,
			queryFragment,
		);

		return (
			<BusinessAppWrapper
				header={header}
				sidebarTheme={isSoftwareFieldsEnabled ? 'software' : 'business'}
				withSidebar
				withOverflow
			>
				<IssueTypesAndFieldsContext>
					<ListViewWithWorkflow queryFragment={data} formattingRulesQuery={formattingRulesQuery} />
					<RefetchFieldsController />
				</IssueTypesAndFieldsContext>
				<JSErrorBoundary
					id="forms-label-flag"
					packageName="jiraBusinessList"
					teamName="jira-werewolves"
				>
					{formsIssueViewExpConfig.get('isFormsIssueViewEnabled', false) && <FormsLabelFlag />}
				</JSErrorBoundary>
			</BusinessAppWrapper>
		);
	},
);

const ListViewRootWithConfluenceProvider = ({ children }: { children: React.ReactElement }) => {
	const hasConfluence = useTenantHasConfluence();

	return hasConfluence ? <ConfluenceModalProvider>{children}</ConfluenceModalProvider> : children;
};

export const ListViewRootWithSmartCard = ({
	queryFragment,
	formattingRulesQuery,
	header,
}: {
	queryFragment: src_businessList_ListViewRootWithSmartCard_queryFragment$key;
	header?: ReactNode;
	formattingRulesQuery?: PreloadedQuery<FormattingRulesQuery> | null;
}) => {
	const hasConfluence = useTenantHasConfluence();

	const data = useFragment(
		graphql`
			fragment src_businessList_ListViewRootWithSmartCard_queryFragment on Query {
				...src_businessList_ListViewRoot_queryFragment
			}
		`,
		queryFragment,
	);

	// `hasConfluence` check only needed until we roll out `jsw_perf_format-links-in-custom-fields` experiment.
	// remove this condition after clean up but keep `SmartCardProvider` & `ListViewRootWithConfluenceProvider`
	if (hasConfluence || expValEquals('jsw_perf_format-links-in-custom-fields', 'list-view', true)) {
		return (
			<SmartCardProvider>
				<ListViewRootWithConfluenceProvider>
					<ApolloClientProvider client={client}>
						<ListViewRoot
							queryFragment={data}
							header={header}
							formattingRulesQuery={formattingRulesQuery}
						/>
					</ApolloClientProvider>
				</ListViewRootWithConfluenceProvider>
			</SmartCardProvider>
		);
	}
	return (
		<ApolloClientProvider client={client}>
			<ListViewRoot
				queryFragment={data}
				header={header}
				formattingRulesQuery={formattingRulesQuery}
			/>
		</ApolloClientProvider>
	);
};

const List = ({
	queryFragment,
	formattingRulesQuery,
	header,
}: {
	queryFragment: src_businessList_List_queryFragment$key;
	header?: ReactNode;
	formattingRulesQuery?: PreloadedQuery<FormattingRulesQuery> | null;
}) => {
	const data = useFragment(
		graphql`
			fragment src_businessList_List_queryFragment on Query {
				...src_businessList_ListViewRootWithSmartCard_queryFragment
			}
		`,
		queryFragment,
	);

	let content = (
		<AuthenticationErrorBoundary render={() => <LoginRedirect />}>
			<ListViewRootWithSmartCard
				queryFragment={data}
				header={header}
				formattingRulesQuery={formattingRulesQuery}
			/>
		</AuthenticationErrorBoundary>
	);

	if (fg('list-handle-field-missing-handler-server-error')) {
		content = (
			<InvalidJQLErrorBoundary render={() => <EmptyState reason="INVALID_JQL" />}>
				{content}
			</InvalidJQLErrorBoundary>
		);
	}

	return (
		<ExperienceErrorBoundary experience={VIEW_EXPERIENCE} shouldStartExperience>
			{content}
		</ExperienceErrorBoundary>
	);
};

export default memo(List);
